import type { SoftRule } from '../../types';

/**
 * Soft Rules: Preferred constraints that should be followed when possible
 * These can be violated if necessary, but violations are penalized based on weight
 */
export const mockSoftRules: SoftRule[] = [
  // Teacher preferences
  {
    id: 'sr-1',
    name: 'Avoid Teacher Split Days',
    description: 'Teachers prefer to work full days rather than split days with gaps',
    category: 'teacher',
    conditions: [
      {
        field: 'teacher.schedule',
        operator: 'minimize_gaps',
        value: null,
      },
    ],
    weight: 8,
    isActive: true,
    createdAt: new Date('2025-01-15'),
    updatedAt: new Date('2025-01-15'),
  },
  {
    id: 'sr-2',
    name: 'Limit Teacher Daily Hours',
    description: 'Prefer not to schedule teachers for more than 5 teaching periods per day',
    category: 'teacher',
    conditions: [
      {
        field: 'teacher.dailyTeachingPeriods',
        operator: 'less_than_or_equal',
        value: 5,
      },
    ],
    weight: 6,
    isActive: true,
    createdAt: new Date('2025-01-15'),
    updatedAt: new Date('2025-01-15'),
  },
  {
    id: 'sr-3',
    name: 'Part-Time Teacher Consolidation',
    description: 'Part-time teachers prefer consolidated schedules (fewer days per week)',
    category: 'teacher',
    conditions: [
      {
        field: 'teacher.isPartTime',
        operator: 'equals',
        value: true,
      },
      {
        field: 'teacher.schedule',
        operator: 'minimize_working_days',
        value: null,
      },
    ],
    weight: 7,
    isActive: true,
    createdAt: new Date('2025-01-15'),
    updatedAt: new Date('2025-01-15'),
  },

  // Student welfare
  {
    id: 'sr-4',
    name: 'Avoid Difficult Subject Sequences',
    description: 'Avoid scheduling multiple difficult subjects consecutively',
    category: 'student',
    conditions: [
      {
        field: 'subject.difficulty',
        operator: 'equals',
        value: 'high',
      },
      {
        field: 'nextPeriod.subject.difficulty',
        operator: 'not_equals',
        value: 'high',
      },
    ],
    weight: 5,
    isActive: true,
    createdAt: new Date('2025-01-15'),
    updatedAt: new Date('2025-01-15'),
  },
  {
    id: 'sr-5',
    name: 'Balanced Daily Load',
    description: 'Students should have a mix of practical and theoretical subjects each day',
    category: 'student',
    conditions: [
      {
        field: 'day.practicalSubjects',
        operator: 'between',
        value: [1, 2],
      },
    ],
    weight: 4,
    isActive: true,
    createdAt: new Date('2025-01-15'),
    updatedAt: new Date('2025-01-15'),
  },
  {
    id: 'sr-6',
    name: 'Minimize Student Travel',
    description: 'Minimize building/floor changes between consecutive lessons',
    category: 'student',
    conditions: [
      {
        field: 'room.building',
        operator: 'same_as',
        value: 'nextPeriod.room.building',
      },
    ],
    weight: 3,
    isActive: true,
    createdAt: new Date('2025-01-15'),
    updatedAt: new Date('2025-01-15'),
  },

  // Room utilization
  {
    id: 'sr-7',
    name: 'Maximize Room Utilization',
    description: 'Prefer to use rooms to near capacity to maximize space efficiency',
    category: 'room',
    conditions: [
      {
        field: 'room.utilization',
        operator: 'greater_than',
        value: 0.7,
      },
    ],
    weight: 4,
    isActive: true,
    createdAt: new Date('2025-01-15'),
    updatedAt: new Date('2025-01-15'),
  },
  {
    id: 'sr-8',
    name: 'Subject-Appropriate Rooms',
    description: 'Prefer to use department-designated rooms even when not strictly required',
    category: 'room',
    conditions: [
      {
        field: 'room.department',
        operator: 'matches',
        value: 'subject.department',
      },
    ],
    weight: 5,
    isActive: true,
    createdAt: new Date('2025-01-15'),
    updatedAt: new Date('2025-01-15'),
  },

  // Curriculum timing
  {
    id: 'sr-9',
    name: 'Core Subjects Early',
    description: 'Schedule Maths and English in morning periods when concentration is highest',
    category: 'curriculum',
    conditions: [
      {
        field: 'subject.isCore',
        operator: 'equals',
        value: true,
      },
      {
        field: 'period.number',
        operator: 'less_than_or_equal',
        value: 3,
      },
    ],
    weight: 6,
    isActive: true,
    createdAt: new Date('2025-01-15'),
    updatedAt: new Date('2025-01-15'),
  },
  {
    id: 'sr-10',
    name: 'Spread Subjects Across Week',
    description: 'Avoid scheduling all lessons of a subject on consecutive days',
    category: 'curriculum',
    conditions: [
      {
        field: 'subject.weeklyDistribution',
        operator: 'spread_evenly',
        value: null,
      },
    ],
    weight: 7,
    isActive: true,
    createdAt: new Date('2025-01-15'),
    updatedAt: new Date('2025-01-15'),
  },
  {
    id: 'sr-11',
    name: 'Same Time Each Week',
    description: 'For repeating lessons, prefer the same day/period each week for consistency',
    category: 'curriculum',
    conditions: [
      {
        field: 'lesson.pattern',
        operator: 'consistent_weekly',
        value: null,
      },
    ],
    weight: 5,
    isActive: true,
    createdAt: new Date('2025-01-15'),
    updatedAt: new Date('2025-01-15'),
  },

  // Department collaboration
  {
    id: 'sr-12',
    name: 'Department Meeting Windows',
    description: 'Keep Wednesday afternoon free for department meetings',
    category: 'collaboration',
    conditions: [
      {
        field: 'day',
        operator: 'equals',
        value: 'wednesday',
      },
      {
        field: 'period.number',
        operator: 'greater_than_or_equal',
        value: 4,
      },
      {
        field: 'teacher.schedule',
        operator: 'is_free',
        value: null,
      },
    ],
    weight: 6,
    isActive: true,
    createdAt: new Date('2025-01-15'),
    updatedAt: new Date('2025-01-15'),
  },
  {
    id: 'sr-13',
    name: 'Parallel Subject Delivery',
    description: 'Same subject for year group should run in parallel periods for cover arrangements',
    category: 'collaboration',
    conditions: [
      {
        field: 'subject',
        operator: 'same_as',
        value: 'parallelLesson.subject',
      },
      {
        field: 'yearGroup',
        operator: 'same_as',
        value: 'parallelLesson.yearGroup',
      },
      {
        field: 'period',
        operator: 'same_as',
        value: 'parallelLesson.period',
      },
    ],
    weight: 4,
    isActive: true,
    createdAt: new Date('2025-01-15'),
    updatedAt: new Date('2025-01-15'),
  },
];

export const getSoftRulesByCategory = (category: SoftRule['category']): SoftRule[] => {
  return mockSoftRules.filter((rule) => rule.category === category);
};

export const getActiveSoftRules = (): SoftRule[] => {
  return mockSoftRules.filter((rule) => rule.isActive);
};

export const getSoftRulesByWeight = (): SoftRule[] => {
  return [...mockSoftRules].sort((a, b) => b.weight - a.weight);
};
